import { useState, useEffect } from "react";
import {
  ActionIcon, Anchor, Badge, Card, Center, Container, Group, Loader, Stack,
  Tabs, Text, ThemeIcon, Title,
} from "@mantine/core";
import TelLink from "../components/TelLink";
import ScrollableTabsList from "../components/ScrollableTabsList";
import {
  IconArrowLeft, IconBriefcase, IconBuilding, IconChevronRight, IconExternalLink, IconUser,
} from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { useLocation, useParams } from "wouter";
import { getClient, getClientProjects } from "../api";
import TasksPanel from "../Tasks/TasksPanel";

const statusColors = { active: "blue", completed: "green", onHold: "yellow" };

export default function ClientDetail() {
  const { t } = useTranslation("Veft");
  const { id } = useParams();
  const [, setLocation] = useLocation();
  const [client, setClient] = useState(null);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, [id]);

  async function loadData() {
    setLoading(true);
    try {
      const [cli, proj] = await Promise.all([getClient(id), getClientProjects(id)]);
      setClient(cli);
      setProjects(Array.isArray(proj) ? proj : []);
    } catch (e) {
      console.error("Failed to load client", e);
    } finally {
      setLoading(false);
    }
  }

  if (loading) return <Center py="xl"><Loader /></Center>;
  if (!client) return <Text c="dimmed" ta="center" py="xl">{t("no_data")}</Text>;

  const contacts = client.contacts ?? [];

  return (
    <Container size="md" py="md">
    <Stack gap="lg">
      <Group>
        <ActionIcon variant="subtle" onClick={() => setLocation("/clients")}>
          <IconArrowLeft size={18} />
        </ActionIcon>
        <ThemeIcon variant="light" size="lg"><IconBuilding size={20} /></ThemeIcon>
        <div>
          <Title order={3}>{client.name}</Title>
          {client.industry && <Text size="sm" c="dimmed">{client.industry}</Text>}
        </div>
      </Group>

      <Tabs defaultValue="overview">
        <ScrollableTabsList>
          <Tabs.Tab value="overview">{t("tab_summary")}</Tabs.Tab>
          <Tabs.Tab value="contacts">{t("contacts")} ({contacts.length})</Tabs.Tab>
          <Tabs.Tab value="projects">{t("projects")} ({projects.length})</Tabs.Tab>
          <Tabs.Tab value="tasks">{t("tab_tasks")}</Tabs.Tab>
        </ScrollableTabsList>

        <Tabs.Panel value="overview" pt="md">
          <Card>
            <Stack gap="xs">
              <Group gap="xs">
                <Text size="sm" fw={500}>{t("company_name")}:</Text>
                <Text size="sm">{client.name}</Text>
              </Group>
              <Group gap="xs">
                <Text size="sm" fw={500}>{t("industry")}:</Text>
                <Text size="sm">{client.industry || t("no_data")}</Text>
              </Group>
              {client.notes && (
                <div>
                  <Text size="sm" fw={500} mb={4}>{t("notes")}:</Text>
                  <Text size="sm" c="dimmed" style={{ whiteSpace: "pre-wrap" }}>{client.notes}</Text>
                </div>
              )}
            </Stack>
          </Card>
        </Tabs.Panel>

        <Tabs.Panel value="contacts" pt="md">
          {contacts.length === 0 ? (
            <Text c="dimmed" ta="center" py="xl">{t("no_contacts")}</Text>
          ) : (
            <Stack gap="xs">
              {contacts.map((c, i) => (
                <Card key={i} p="sm" withBorder>
                  <Group gap="sm">
                    <ThemeIcon variant="light" size="sm"><IconUser size={14} /></ThemeIcon>
                    <div style={{ flex: 1 }}>
                      {c.linkedCandidateId ? (
                        <Text
                          size="sm"
                          fw={500}
                          c="blue"
                          style={{ cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4 }}
                          onClick={() => setLocation(`/candidates/${c.linkedCandidateId}`)}
                        >
                          {c.name}
                          <IconExternalLink size={12} />
                        </Text>
                      ) : (
                        <Text size="sm" fw={500}>{c.name}</Text>
                      )}
                      {c.role && <Text size="xs" c="dimmed">{c.role}</Text>}
                      <Group gap="xs" wrap="wrap" mt={2}>
                        {c.mobilePhone && <TelLink label={t("mobile")} value={c.mobilePhone} />}
                        {c.directDial && <TelLink label={t("direct_dial")} value={c.directDial} />}
                        {c.phone && <TelLink label={t("phone")} value={c.phone} />}
                        {c.email && (
                          <Text size="xs" c="dimmed">
                            <Anchor href={`mailto:${c.email}`}>{c.email}</Anchor>
                          </Text>
                        )}
                      </Group>
                      {c.assistantName && (
                        <Text size="xs" c="dimmed" mt={2}>
                          {t("contact_assistant", { name: c.assistantName })}
                          {c.assistantPhone ? ` (${t("phone")}: ${c.assistantPhone})` : ""}
                          {c.assistantMobile ? ` (${t("mobile")}: ${c.assistantMobile})` : ""}
                        </Text>
                      )}
                    </div>
                  </Group>
                </Card>
              ))}
            </Stack>
          )}
        </Tabs.Panel>

        <Tabs.Panel value="projects" pt="md">
          {projects.length === 0 ? (
            <Text c="dimmed" ta="center" py="xl">{t("no_projects")}</Text>
          ) : (
            <Stack gap="xs">
              {projects.map((project) => (
                <Card
                  key={project.id}
                  p="sm"
                  withBorder
                  style={{ cursor: "pointer" }}
                  onClick={() => setLocation(`/projects/${project.id}`)}
                >
                  <Group justify="space-between">
                    <Group>
                      <ThemeIcon variant="light" size="sm"><IconBriefcase size={14} /></ThemeIcon>
                      <div>
                        <Text size="sm" fw={500}>{project.title}</Text>
                        {project.roleTitle && <Text size="xs" c="dimmed">{project.roleTitle}</Text>}
                      </div>
                    </Group>
                    <Group gap="xs">
                      <Badge size="xs" color={statusColors[project.status] ?? "gray"}>{project.status}</Badge>
                      <Text size="xs" c="dimmed">
                        {new Date(project.updatedAt).toLocaleDateString("de-DE")}
                      </Text>
                      <IconChevronRight size={14} color="gray" />
                    </Group>
                  </Group>
                </Card>
              ))}
            </Stack>
          )}
        </Tabs.Panel>

        <Tabs.Panel value="tasks" pt="md">
          <TasksPanel clientId={id} />
        </Tabs.Panel>
      </Tabs>
    </Stack>
    </Container>
  );
}
